// Helper function to reorganize groups: sort all by first page number
const reorganizeGroups = (groups: number[][]) => {
  const multiPageGroups: number[][] = [];
  const singlePages: number[] = [];

  // Separate multi-page groups from single pages
  groups.forEach((group) => {
    if (group.length > 1) {
      multiPageGroups.push(group);
    } else {
      singlePages.push(...group);
    }
  });

  // Sort single pages numerically
  singlePages.sort((a, b) => a - b);

  // Sort multi-page groups by their first page number
  multiPageGroups.sort((a, b) => Math.min(...a) - Math.min(...b));

  if (multiPageGroups.length === 0) {
    return singlePages.map((page) => [page]);
  }

  if (singlePages.length === 0) {
    return multiPageGroups;
  }

  const result: number[][] = [];
  let singleIndex = 0;
  let groupIndex = 0;

  // Add groups and singles in proper numerical order
  while (
    singleIndex < singlePages.length ||
    groupIndex < multiPageGroups.length
  ) {
    const hasMoreSingles = singleIndex < singlePages.length;
    const hasMoreGroups = groupIndex < multiPageGroups.length;

    if (!hasMoreSingles && hasMoreGroups) {
      result.push(multiPageGroups[groupIndex]);
      groupIndex++;
    } else if (hasMoreSingles && !hasMoreGroups) {
      result.push([singlePages[singleIndex]]);
      singleIndex++;
    } else if (hasMoreSingles && hasMoreGroups) {
      const nextSinglePage = singlePages[singleIndex];
      const nextGroup = multiPageGroups[groupIndex];
      const nextGroupFirstPage = Math.min(...nextGroup);

      if (nextSinglePage < nextGroupFirstPage) {
        result.push([nextSinglePage]);
        singleIndex++;
      } else {
        result.push(nextGroup);
        groupIndex++;
      }
    }
  }

  return result;
};

const mergeWithAdjacent = (
  pageGroups: number[][],
  groupIndex: number,
  direction: 'prev' | 'next'
) => {
  const targetIndex = direction === 'prev' ? groupIndex - 1 : groupIndex + 1;

  if (targetIndex < 0 || targetIndex >= pageGroups.length) return pageGroups;

  const newGroups = [...pageGroups];
  const group1 = newGroups[Math.min(groupIndex, targetIndex)];
  const group2 = newGroups[Math.max(groupIndex, targetIndex)];

  const mergedGroup = [...group1, ...group2];

  newGroups.splice(Math.min(groupIndex, targetIndex), 2, mergedGroup);

  return reorganizeGroups(newGroups);
};

const splitPageFromGroups = (pageGroups: number[][], pageNum: number) => {
  const groupIndex = pageGroups.findIndex((group) => group.includes(pageNum));
  if (groupIndex === -1) return pageGroups;

  const group = pageGroups[groupIndex];
  if (group.length === 1) return pageGroups;

  const newGroups = [...pageGroups];
  const pageIndex = group.indexOf(pageNum);

  // Split at this page: everything before stays, this page and after go to new group
  const beforePages = group.slice(0, pageIndex);
  const afterPages = group.slice(pageIndex);

  if (beforePages.length > 0 && afterPages.length > 0) {
    newGroups.splice(groupIndex, 1, beforePages, afterPages);
  }

  return reorganizeGroups(newGroups);
};

const splitSelectedGroups = (
  pageGroups: number[][],
  selectedPages: number[],
  totalPages: number // TODO Can this be determined from pageGroups? Does it work with deleted pages?
) => {
  if (selectedPages.length === 0) return pageGroups;

  // const totalPages = pageGroups.flat().length;

  let currentGroups = pageGroups;
  if (currentGroups.length === 0 && totalPages > 0) {
    currentGroups = Array.from({ length: totalPages }, (_, i) => [i + 1]);
  }

  const groupsToSplit = new Set<number>();
  selectedPages.forEach((page) => {
    const groupIndex = currentGroups.findIndex((group) => group.includes(page));
    if (groupIndex !== -1 && currentGroups[groupIndex].length > 1) {
      groupsToSplit.add(groupIndex);
    }
  });

  if (groupsToSplit.size === 0) return pageGroups;

  const newGroups: number[][] = [];
  currentGroups.forEach((group, index) => {
    if (groupsToSplit.has(index)) {
      group.forEach((page) => newGroups.push([page]));
    } else {
      newGroups.push(group);
    }
  });

  return reorganizeGroups(newGroups);
};

const mergeSelectedPages = (
  pageGroups: number[][],
  selectedPages: number[],
  totalPages: number // TODO Can this be determined from pageGroups? Does it work with deleted pages?
) => {
  if (selectedPages.length < 2) return pageGroups;

  // const totalPages = pageGroups.flat().length;

  let currentGroups = pageGroups;
  if (currentGroups.length === 0 && totalPages > 0) {
    currentGroups = Array.from({ length: totalPages }, (_, i) => [i + 1]);
  }

  const filteredGroups = currentGroups
    .map((group) => group.filter((page) => !selectedPages.includes(page)))
    .filter((group) => group.length > 0);

  const newGroup = selectedPages.sort((a, b) => a - b);
  filteredGroups.push(newGroup);

  return reorganizeGroups(filteredGroups);
};

export {
  reorganizeGroups,
  mergeWithAdjacent,
  splitPageFromGroups,
  splitSelectedGroups,
  mergeSelectedPages,
};
